import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { Users, Target, Lightbulb, Globe, Award, Heart } from 'lucide-react';

export function About() {
  const { theme } = useTheme();

  const values = [
    {
      icon: Target,
      title: "Mission-Driven",
      description: "We are committed to advancing artificial intelligence for the benefit of humanity."
    },
    {
      icon: Lightbulb,
      title: "Innovation First",
      description: "We push the boundaries of what's possible in artificial intelligence research."
    },
    {
      icon: Users,
      title: "Collaborative",
      description: "We believe in the power of diverse teams working together towards common goals."
    },
    {
      icon: Globe,
      title: "Global Impact",
      description: "Our research and solutions are designed to address challenges on a global scale."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We maintain the highest standards in research, ethics, and technological development."
    },
    {
      icon: Heart,
      title: "Responsible AI",
      description: "We are committed to developing safe, beneficial, and ethical artificial intelligence."
    }
  ];

  const team = [
    {
      name: "Dr. Sarah Chen",
      role: "Chief Executive Officer",
      description: "Former MIT researcher with expertise in neural networks and machine learning.",
      image: "/api/placeholder/300/300"
    },
    {
      name: "Dr. Marcus Rodriguez",
      role: "Chief Technology Officer", 
      description: "Pioneer in deep learning architectures and AI safety research.",
      image: "/api/placeholder/300/300"
    },
    {
      name: "Dr. Aisha Patel",
      role: "Head of Research",
      description: "Leading expert in natural language processing and cognitive computing.",
      image: "/api/placeholder/300/300"
    },
    {
      name: "Dr. James Thompson",
      role: "Director of AI Safety",
      description: "Specialist in AI alignment and responsible AI development practices.",
      image: "/api/placeholder/300/300"
    }
  ];

  const milestones = [
    {
      year: "2025",
      title: "Start Huxley Research",
      description: "Established with a mission to advance artificial intelligence and computational biology for humanity."
    },
    {
      year: "2025",
      title: "FRACTAL 3B Launch",
      description: "Released our protein structure prediction system with constraint-based folding architecture."
    },
    {
      year: "2025",
      title: "Huxley Framework Release",
      description: "Launched Huxley, a model-agnostic framework for long-horizon biological AI systems."
    }
  ];

  return (
    <div className="relative">
      <Background3D />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-semibold mb-8 leading-tight">
              About <span className="text-gradient">Huxley Research</span>
            </h1>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              We are a research organization dedicated to advancing artificial intelligence 
              in ways that benefit humanity. Our mission is to solve intelligence and use 
              it to address the world's most pressing challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className={`rounded-3xl p-12 md:p-16 text-center ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-primary-500/10 to-secondary-500/10 border border-primary-500/20'
              : 'bg-gradient-to-br from-primary-50 to-secondary-50 border border-primary-200'
          }`}>
            <h2 className="text-3xl md:text-4xl font-semibold mb-8">
              Our Mission
            </h2>
            <p className={`text-lg md:text-xl leading-relaxed max-w-4xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              To advance the field of artificial intelligence through groundbreaking research, 
              responsible development practices, and innovative applications that solve real-world 
              problems. We believe that AI should augment human capabilities and contribute to 
              a more equitable and sustainable future for all.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Our Values
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={`p-8 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300 hover:border-primary-500'
                    : 'bg-white/70 border-gray-200 hover:border-primary-300'
                } backdrop-blur-sm`}
              >
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 ${
                  theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                }`}>
                  <value.icon className="w-7 h-7 text-primary-500" />
                </div>
                <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                <p className={`leading-relaxed ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-16 text-center">
            Our Journey
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className={`absolute left-8 top-0 bottom-0 w-0.5 ${
                theme === 'dark' ? 'bg-primary-500/30' : 'bg-primary-300'
              }`}></div>
              
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex items-start mb-12 last:mb-0">
                  {/* Timeline dot */}
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-sm mr-8 ${
                    theme === 'dark' ? 'bg-primary-600' : 'bg-primary-500'
                  }`}>
                    {milestone.year}
                  </div>
                  
                  {/* Content */}
                  <div className={`flex-1 p-6 rounded-2xl ${
                    theme === 'dark' ? 'bg-dark-200/50' : 'bg-white/70'
                  } backdrop-blur-sm`}>
                    <h3 className="text-xl font-semibold mb-3">{milestone.title}</h3>
                    <p className={`leading-relaxed ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className={`py-20 ${
        theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'
      }`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-16 text-center">
            Leadership Team
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border transition-all duration-300 hover:scale-105 ${
                  theme === 'dark'
                    ? 'bg-dark-200/50 border-dark-300'
                    : 'bg-white/70 border-gray-200'
                } backdrop-blur-sm`}
              >
                <div className={`w-24 h-24 rounded-full mx-auto mb-6 ${
                  theme === 'dark' ? 'bg-dark-300' : 'bg-gray-200'
                } flex items-center justify-center`}>
                  <Users className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-center">{member.name}</h3>
                <p className={`text-sm font-medium mb-3 text-center ${
                  theme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                }`}>
                  {member.role}
                </p>
                <p className={`text-sm leading-relaxed text-center ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {member.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <div className={`rounded-2xl p-12 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-dark-200 to-dark-300/50'
              : 'bg-gradient-to-br from-gray-50 to-white'
          }`}>
            <h2 className="text-3xl font-semibold mb-4">
              Join Our Mission
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Whether you're a researcher, developer, or simply passionate about AI, 
              we invite you to be part of our journey to advance intelligence for humanity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/careers"
                className="px-8 py-4 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
              >
                View Open Positions
              </a>
              <a
                href="/company"
                className={`px-8 py-4 rounded-full font-medium transition-colors ${
                  theme === 'dark'
                    ? 'bg-dark-300 text-white hover:bg-dark-400'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
